'use client';

import { BellIcon, CartIcon } from '@wolfejs/icons/IconMap';
import type { UserEntity } from '@wolfejs/ms-api-js/src/types/users';
import CartButton from '@wolfejs/ms-ui/Cart/CartButton/CartButton';
import { Avatar } from '@wolfejs/ui/Avatar';
import { Button } from '@wolfejs/ui/Button/Button';
import { FormatCurrency } from '@wolfejs/ui/Format';
import type { MainMenuItemRecursive, MenuItemType } from '@wolfejs/ui/Menu';
import { Menu, MenuItem } from '@wolfejs/ui/Menu';
import { PopoverMenu } from '@wolfejs/ui/PopoverMenu/PopoverMenu';
import { ThemeModeToggleContainer } from '@wolfejs/ui/ThemeModeToggle/ThemeModeToggleContainer';
import { LayoutTypes, UISizes, UIVariants } from '@wolfejs/ui/types/UI';
import cn from 'classnames';
import Link from 'next/link';
import type { MouseEventHandler } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { HeaderLayout } from './types';

export type AppHeaderCommonProps = {
  layout: HeaderLayout;
  brandLogo: React.ReactNode;
  onSignOutClick?: () => void;
  popoverUserMenuItems: MainMenuItemRecursive[];
  routes: {
    home: string;
    shoppingCart?: string;
  };
  user?: UserEntity;
  slug?: string | string[];
  availableFunds?: number;
  showShoppingCart?: boolean;
};

export type AppHeaderProps = AppHeaderCommonProps & {
  showAvailableBalance?: boolean;
  menuOptions?: MainMenuItemRecursive[];
  themeModeToggle?: boolean;
  onNotificationClick?: MouseEventHandler<HTMLButtonElement>;
  onCartClick?: MouseEventHandler<HTMLButtonElement>;
};

export const AppHeader = ({
  layout,
  brandLogo,
  popoverUserMenuItems,
  onSignOutClick,
  routes,
  user,
  showShoppingCart,
  themeModeToggle,
  slug,
  showAvailableBalance = true,
  ...otherProps
}: AppHeaderProps) => {
  const handleMenuSelect = React.useCallback(
    (item: MenuItemType) => {
      switch (item.id) {
        case 'logout':
          onSignOutClick?.();
          break;
      }
    },
    [onSignOutClick]
  );
  const { t } = useTranslation();

  const normal = (
    <div className={cn('not-prose bg-background container relative flex items-center justify-between py-4')}>
      <Link href={`/${slug}${routes.home}`}>{brandLogo}</Link>

      {otherProps.menuOptions && (
        <Menu
          variant={UIVariants.ghost}
          layout={LayoutTypes.horizontal}
          items={otherProps?.menuOptions}
          renderItem={(item: MainMenuItemRecursive) => (
            <Link href={`/${slug}${item.url}`} className="text-foreground">
              <MenuItem item={item} />
            </Link>
          )}
        />
      )}

      <div className={cn('flex items-center gap-2')}>
        {themeModeToggle && <ThemeModeToggleContainer />}
        <Button size={UISizes.sm} variant={UIVariants.ghost} onClick={otherProps?.onNotificationClick}>
          <BellIcon />
        </Button>
        <PopoverMenu
          trigger={<Avatar alt="user-avatar" shouldFallbackToInitials fullname={user?.name} />}
          className="not-prose"
          onSelect={handleMenuSelect}
          closeOnSelect
          items={popoverUserMenuItems}
          renderItem={item => {
            if (item?.url) {
              return (
                <Link href={`/${slug}${item.url}`}>
                  <MenuItem item={item} />
                </Link>
              );
            } else {
              return <MenuItem item={item} />;
            }
          }}
        />
        <Button size={UISizes.sm} variant={UIVariants.ghost} onClick={otherProps?.onCartClick}>
          <CartIcon />
        </Button>
      </div>
    </div>
  );
  const minimal = (
    <div className="not-prose dark:bg-default-900 relative flex h-16 w-full items-center justify-between bg-white px-4">
      <div className="items-align flex gap-3">
        <div>
          <Link href={`/${slug}${routes.home}`}>{brandLogo}</Link>
        </div>
        <div className="relative flex h-8 w-px flex-col justify-center">
          <div className="bg-default-200 absolute left-0 top-0 h-8 w-px" />
        </div>
        <div className="flex flex-col justify-center">
          <div className="text-default-900 text-base font-semibold dark:text-white">{t('business')}</div>
          {showAvailableBalance && (
            <div className="text-default-400 text-xs font-normal">
              {t('funds.availableBalance')}
              <span className="text-sm font-normal text-gray-500">
                {' '}
                <FormatCurrency
                  prefix="$"
                  decimalScale={2}
                  numberFormatBaseProps={{ value: otherProps?.availableFunds || 0, displayType: 'text' }}
                />
              </span>
            </div>
          )}
        </div>
      </div>
      <div className={cn('flex items-center gap-2')}>
        {themeModeToggle && <ThemeModeToggleContainer />}
        {showShoppingCart && (
          <Link href={`/${slug}${routes.shoppingCart}`} className="text-foreground">
            <CartButton />
          </Link>
        )}
        <PopoverMenu
          variant="outline"
          trigger={<Avatar alt="user-avatar" shouldFallbackToInitials fullname={user?.name} />}
          className="dark:bg-default-900 not-prose border-default-100 rounded-lg border bg-white"
          classNames={{ box: 'border-default-100' }}
          onSelect={handleMenuSelect}
          closeOnSelect
          items={popoverUserMenuItems}
          renderItem={item => {
            if (item?.url) {
              return (
                <Link href={`/${slug}${item.url}`}>
                  <MenuItem item={item} className="border-default-100 border border-b" />
                </Link>
              );
            } else {
              return <MenuItem item={item} className="border-default-100 border border-b" />;
            }
          }}
        />
      </div>
    </div>
  );
  return layout === HeaderLayout.Normal ? normal : minimal;
};
